import { put, call, takeLatest } from 'redux-saga/effects';
import * as Api from './api';
import { ListingViewConstants } from '@/common/constants';
import { SHOW_LOADER, HIDE_LOADER, SESSIONCONTEXT_DATA } from '@/common/actionConstants';
import { logout } from '@/containers/WorkkardDetail/Saga/common';
import { hydrateUsers } from '@/common/sagas/userListSaga';
import { LOG_APPLICATON_ERROR } from '@/containers/WorkkardDetail/actions/actionConstants';
import { APP_ERROR_CODES, ErrorCode } from '@/containers/WorkkardDetail/constants';
import { normalizeTabCountResponse, normalizeListingResponse } from '@/services/workkardListing';
import {
  fetchWorkkardTabCountRequest,
  fetchWorkkardTabCountSuccess,
  fetchWorkkardTabCountFailure,
  fetchWorkkardListingDataRequest,
  fetchWorkkardListingDataSuccess,
  fetchWorkkardListingDataFailure,
  updatePagingModel,
  resetWorkkardListingData,
} from './slice';

// A live refresh passes `silent` so the counts update in place, without the
// full-screen loader a user-driven load shows.
export function* getWorkkardListingTabCount(action) {
  const silent = !!action?.payload?.silent;
  try {
    if (!silent) yield put({ type: SHOW_LOADER });

    const response = yield call(Api.getWorkkardListingTabCount);

    if (response.status === 200) {
      const normalized = normalizeTabCountResponse(response);
      
      if (normalized.sessionContext) {
        yield put({ type: SESSIONCONTEXT_DATA, payload: normalized.sessionContext });
      }
      
      yield put(fetchWorkkardTabCountSuccess(normalized.tabCounts));
    } else if (response.status === ErrorCode.RedirectToLogout) {
      yield* logout();
    } else if (APP_ERROR_CODES.indexOf(response.status) > -1) {
      yield put({
        type: LOG_APPLICATON_ERROR,
        payload: { status: response.status, isErrorScreen: true },
      });
      yield put(fetchWorkkardTabCountFailure({ status: response.status }));
    } else {
      console.error('[TabCount] Unexpected status:', response.status);
    }
  } catch (error) {
    console.error('[TabCount] Error:', error);
    yield put(fetchWorkkardTabCountFailure({ error: error.message }));
  } finally {
    if (!silent) yield put({ type: HIDE_LOADER });
  }
}

// A `silent` fetch (a live socket refresh) shows no loader and keeps the current
// rows on screen until the response replaces them, so the list does not flash
// empty.
export function* getWorkkardListingData(action) {
  const { view, isScroll, silent, ...apiPayload } = action.payload;
  try {
    if (!silent) yield put({ type: SHOW_LOADER });

    const isListView = view === ListingViewConstants.ListView;

    if (!isScroll && !silent) {
      yield put(resetWorkkardListingData());
    }

    const response = yield call(Api.getWorkkardListingData, isListView, apiPayload);

    if (response.status === 200) {
      const normalized = normalizeListingResponse(response, isScroll);
      
      yield put(updatePagingModel({
        page: apiPayload.page,
        totalCount: normalized.total,
        rows: apiPayload.rows,
      }));

      yield put(fetchWorkkardListingDataSuccess({
        ...normalized,
        isScroll,
      }));

      const workkardUserIds = (normalized.workkardsFullData || []).reduce((acc, item) => {
        if (item.sponsorId) acc.push(item.sponsorId);
        if (item.workkardOwnerId && item.workkardOwnerId !== item.sponsorId) {
          acc.push(item.workkardOwnerId);
        }
        return acc;
      }, []);
      if (workkardUserIds.length > 0) {
        yield* hydrateUsers(workkardUserIds);
      }
    } else if (response.status === ErrorCode.RedirectToLogout) {
      yield* logout();
    } else if (APP_ERROR_CODES.indexOf(response.status) > -1) {
      yield put({
        type: LOG_APPLICATON_ERROR,
        payload: { status: response.status, isErrorScreen: true },
      });
      yield put(fetchWorkkardListingDataFailure({ status: response.status }));
    }
  } catch (error) {
    yield put(fetchWorkkardListingDataFailure({ error: error.message }));
  } finally {
    if (!silent) yield put({ type: HIDE_LOADER });
  }
}

export default [
  takeLatest(fetchWorkkardTabCountRequest.type, getWorkkardListingTabCount),
  takeLatest(fetchWorkkardListingDataRequest.type, getWorkkardListingData),
];
